import React, { useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  Download,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import Button from "../Button/Button";
import ResponseMessage from "../ResponseMessage/ResponseMessage";

interface ThemeColors {
  primary?: string;
  textPrimary?: string;
  textSecondary?: string;
  borderColor?: string;
  backgroundTertiary?: string;
  [key: string]: unknown;
}

interface Message {
  id?: string;
  type?: string;
  role?: string;
  content?: string | { message?: string };
  file?: { name?: string; type?: string; url?: string };
  [key: string]: unknown;
}

interface ChatContainerProps {
  theme: ThemeColors;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (v: boolean) => void;
  currentChatId: string | null;
  chatHistory: Record<string, { title?: string; agent_type?: string; messages?: Message[] }>;
  exportCurrentChat: () => void;
  isTyping: boolean;
  messages: Message[];
  user?: { email?: string; name?: string; picture?: string };
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  showFilePreview: boolean;
  pendingFile: { name: string; type: string; url: string } | null;
  setPendingFile: (f: { name: string; type: string; url: string } | null) => void;
  setShowFilePreview: (v: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputText: string;
  setInputText: (v: string) => void;
  handleSendMessage: () => void;
}

const ChatContainer = ({
  theme,
  isSidebarCollapsed,
  setIsSidebarCollapsed,
  currentChatId,
  chatHistory,
  exportCurrentChat,
  isTyping,
  messages,
  user,
  messagesEndRef,
  showFilePreview,
  pendingFile,
  setPendingFile,
  setShowFilePreview,
  fileInputRef,
  handleFileUpload,
  inputText,
  setInputText,
  handleSendMessage,
}: ChatContainerProps): React.ReactElement => {
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  const normalizeMessageContent = (content: unknown): string => {
    if (typeof content === "string") return content;

    if (content && typeof content === "object") {
      const c = content as { message?: string };
      if (typeof c.message === "string") {
        return c.message;
      }

      try {
        return JSON.stringify(content, null, 2);
      } catch {
        return "";
      }
    }

    return "";
  };

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null): boolean => {
      const element = target as HTMLElement | null;
      if (!element) return false;
      const tagName = element.tagName.toLowerCase();
      return (
        element.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(element.closest('input, textarea, select, [contenteditable="true"]'))
      );
    };

    const handleKeyboardShortcuts = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;

      if (event.key === "Escape") {
        const activeElement = document.activeElement as HTMLElement | null;
        if (activeElement && isEditableTarget(activeElement)) {
          activeElement.blur();
        }
        return;
      }

      if (event.key !== " " || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      if (document.querySelector(".search-input-container")) return;

      messageInputRef.current?.focus();
      event.preventDefault();
    };

    document.addEventListener("keydown", handleKeyboardShortcuts);
    return () => {
      document.removeEventListener("keydown", handleKeyboardShortcuts);
    };
  }, []);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      <div
        className="flex items-center justify-between p-4 border-b"
        style={{ borderColor: theme.borderColor }}
      >
        <div className="flex items-center">
          <Button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="p-1 mr-3"
          >
            {isSidebarCollapsed ? (
              <ChevronRight size={18} color={theme.textPrimary} />
            ) : (
              <ChevronLeft size={18} color={theme.textPrimary} />
            )}
          </Button>
          <h2 className="font-semibold" style={{ color: theme.textPrimary }}>
            {currentChatId && chatHistory[currentChatId]
              ? chatHistory[currentChatId].title
              : "New Conversation"}
          </h2>
        </div>

        <div className="flex items-center space-x-3">
          <Button
            disabled={isTyping}
            className="p-1"
            style={{
              opacity: isTyping ? 0.5 : 1,
            }}
          >
            <RotateCcw size={18} color={theme.textPrimary} />
          </Button>
          <Button onClick={exportCurrentChat} className="p-1">
            <Download size={18} color={theme.textPrimary} />
          </Button>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5"
        data-chat-scroll-container="true"
      >
        {messages.length === 0 ? (
          chatHistory[currentChatId!]?.agent_type === "CreateEventAgent" ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <h1
                className="text-3xl font-bold mb-4"
                style={{ color: theme.primary }}
              >
                Welcome to SuperiorDataScience AI
              </h1>
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: theme.primary }}
              >
                Crud Supervisor Calendar Agent
              </h2>
              <div className="max-w-2xl">
                <p
                  className="text-lg mb-8 leading-relaxed"
                  style={{ color: theme.textSecondary }}
                >
                  I'm here to help you create a new event. Make sure to list the
                  event name, task, goal, time, and instructions on recurrence.
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {[
                    "Create a birthday party reminder",
                    "Schedule a weekly team meeting",
                    "Set up a monthly budget review",
                    "Plan a quarterly performance review",
                  ].map((text) => (
                    <Button
                      key={text}
                      onClick={() => {
                        setInputText(text);
                        setTimeout(() => {
                          handleSendMessage();
                        }, 100);
                      }}
                      className="px-4 py-2 rounded-full border text-sm hover:bg-gray-100"
                      style={{
                        backgroundColor: theme.backgroundTertiary,
                        borderColor: theme.borderColor,
                        color: theme.textPrimary,
                      }}
                    >
                      {text}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : ["ReceiveMessageAgent", "receive_message_agent"].includes(
              String(chatHistory[currentChatId!]?.agent_type || ""),
            ) ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: theme.primary }}
              >
                Welcome to SuperiorDataScience AI
              </h2>
              <h3
                className="text-2xl font-semibold mb-4"
                style={{ color: theme.primary }}
              >
                Receive Message Agent
              </h3>
              <p
                className="text-lg mb-8"
                style={{ color: theme.textSecondary }}
              >
                Manage CRM conversations, inbound messages, and follow-ups. Receive messages from Telnyx and process them.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  "Read my emails",
                  "Send an email",
                  "Show me unread CRM messages",
                  "Summarize latest inbound leads",
                  "Draft follow-up for today's prospects",
                  "Find contacts who need a callback",
                ].map((text) => (
                  <Button
                    key={text}
                    onClick={() => {
                      setInputText(text);
                      setTimeout(() => {
                        handleSendMessage();
                      }, 100);
                    }}
                    className="px-4 py-2 rounded-full border text-sm hover:bg-gray-100"
                    style={{
                      backgroundColor: theme.backgroundTertiary,
                      borderColor: theme.borderColor,
                      color: theme.textPrimary,
                    }}
                  >
                    {text}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: theme.primary }}
              >
                Welcome to SuperiorDataScience AI
              </h2>
              <p
                className="text-lg mb-8"
                style={{ color: theme.textSecondary }}
              >
                Ask me anything. I'm powered by LLMs.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  "Tell me a story",
                  "Explain quantum computing",
                  "Write a poem",
                  "Help me learn JavaScript",
                ].map((text) => (
                  <Button
                    key={text}
                    onClick={() => {
                      setInputText(text);
                      setTimeout(() => {
                        handleSendMessage();
                      }, 100);
                    }}
                    className="px-4 py-2 rounded-full border text-sm hover:bg-gray-100"
                    style={{
                      backgroundColor: theme.backgroundTertiary,
                      borderColor: theme.borderColor,
                      color: theme.textPrimary,
                    }}
                  >
                    {text}
                  </Button>
                ))}
              </div>
            </div>
          )
        ) : (
          <div key={JSON.stringify(messages)}>
            {messages.map((msg, index) => {
              const safeContent = normalizeMessageContent(msg.content);
              const markdownContent = String(safeContent).replace(
                /\[([^\]]+)\]/g,
                "$$$1$$",
              );
              const isHumanMessage = msg.type === "human";

              return (
                <div key={msg.id || `msg-${index}`}>
                  <ResponseMessage
                    message={msg}
                    content={markdownContent}
                    theme={theme}
                    user={user}
                    role={isHumanMessage ? "human" : "ai"}
                  />
                </div>
              );
            })}
            {isTyping && (
              <div className="flex justify-center py-4">
                <div className="flex space-x-1">
                  {[0, 1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-2 h-2 rounded-full animate-pulse"
                      style={{
                        backgroundColor: theme.primary,
                        opacity: 0.6 + i * 0.1,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="p-4 border-t" style={{ borderColor: theme.borderColor }}>
        {showFilePreview && pendingFile && (
          <div
            className="mb-3 p-3 border rounded-lg flex items-center justify-between"
            style={{ borderColor: theme.borderColor }}
          >
            <div className="flex items-center">
              {pendingFile.type.startsWith("image/") ? (
                <img
                  src={pendingFile.url}
                  alt={pendingFile.name}
                  className="w-12 h-12 object-cover rounded mr-3"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center mr-3">
                  <Paperclip size={20} />
                </div>
              )}
              <span className="text-sm" style={{ color: theme.textPrimary }}>
                {pendingFile.name}
              </span>
            </div>
            <Button
              onClick={() => {
                setPendingFile(null);
                setShowFilePreview(false);
              }}
              className="text-red-500 hover:bg-red-50 p-1 rounded"
            >
              <X size={16} />
            </Button>
          </div>
        )}

        <div
          className="flex items-center rounded-lg overflow-hidden"
          style={{ backgroundColor: theme.backgroundTertiary }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="*/*"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 hover:bg-gray-100"
          >
            <Paperclip size={20} color={theme.textSecondary} />
          </Button>

          <textarea
            ref={messageInputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type your message here..."
            className="flex-1 p-3 resize-none outline-non  max-h-[30vh]"
            title="Focus chat input (Space)"
            style={{
              backgroundColor: theme.backgroundTertiary,
              color: theme.textPrimary,
            }}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
          />

          <Button
            onClick={() => handleSendMessage()}
            disabled={!inputText.trim() && !pendingFile}
            className="p-3 text-white disabled:opacity-50"
            style={{ backgroundColor: theme.primary }}
          >
            <Send size={18} />
          </Button>
        </div>

        <p
          className="text-xs text-center mt-2"
          style={{ color: theme.textSecondary }}
        >
          SuperiorDataScience ChatBot may produce inaccurate information.
          Messages are stored in the cloud.
        </p>
      </div>
    </div>
  );
};

export default ChatContainer;
